/**
 * Compose: choose the template → who it goes to → preview the merged words in the recipient's language →
 * Send. Send queues it (the cancel window); the Outbox can take it back until it leaves. A tenant is
 * offered only when they asked for email; internal staff come from Admin → Settings. Unknown [Tokens]
 * block Send with the reason.
 */
import { SpanishWording } from './SpanishWording';
import { useEffect, useMemo, useState, type RefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ComposeContext, PreviewResponse } from '@shared/email';
import { splitHeading } from '@shared/wording';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatClockOf } from '@/lib/dates';
import { pingDispatcher } from '@/lib/messages';
import { useAnnounce } from '@/components/Announcer';
import { ChangeInSettings } from '@/components/ChangeInSettings';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { ReadAloud } from '@/components/ReadAloud';
import { SheetContent } from '@/components/ui/sheet';

export interface ComposeOptions {
  tenantId?: string | null;
  appointmentId?: string | null;
  templateCode?: string;
}

const errorText = (e: unknown, fallback: string) => (e instanceof ApiRequestError ? (e.body?.message ?? fallback) : fallback);

export function ComposeSheet({ opts, returnFocusTo, onDone }: { opts: ComposeOptions; returnFocusTo: RefObject<HTMLElement | null>; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const qs = new URLSearchParams();
  if (opts.tenantId) qs.set('tenantId', opts.tenantId);
  if (opts.appointmentId) qs.set('appointmentId', opts.appointmentId);
  const ctx = useQuery({ queryKey: ['compose', opts.tenantId ?? null, opts.appointmentId ?? null], queryFn: () => apiFetch<ComposeContext>(`/api/messages/compose?${qs.toString()}`) });

  const [code, setCode] = useState(opts.templateCode ?? '');
  const [to, setTo] = useState<string[]>([]);
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const [message, setMessage] = useState('');
  // D-1028-ONELANG-8: the Spanish wording of the message, asked for when this send goes to a Spanish reader
  const [messageEs, setMessageEs] = useState('');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewKey, setPreviewKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const c = ctx.data;
  const templates = useMemo(() => (c?.templates ?? []).filter((x) => x.link !== 'tenant-confirm' || !!c?.appointment), [c]);
  useEffect(() => {
    if (!c) return;
    if (!code) setCode(templates.find((x) => x.code === (c.appointment ? 'HK_PLEASE_CONFIRM' : 'HK_NOTE'))?.code ?? templates[0]?.code ?? '');
    setLang(c.tenant?.lang ?? 'en');
    const tenant = c.recipients.find((r) => r.kind === 'tenant' && r.allowed);
    if (tenant) setTo([tenant.address]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c]);

  const key = JSON.stringify({ code, to, lang, message, messageEs });
  const stale = !preview || previewKey !== key;
  const body = () => JSON.stringify({ templateCode: code, tenantId: c?.tenant?.tenantId ?? null, appointmentId: c?.appointment?.appointmentId ?? null, to, lang, message: message || undefined, messageEs: messageEs || undefined });

  const doPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await apiFetch<PreviewResponse>('/api/messages/preview', { method: 'POST', body: body() });
      setPreview(p);
      setPreviewKey(key);
      announce(p.blocked ? p.blocked : t('email.previewReady'), p.blocked ? 'assertive' : 'polite');
    } catch (e) {
      const m = errorText(e, t('email.previewFailed'));
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<{ communicationId: string | null; status: string; scheduledAt: string }>('/api/messages', { method: 'POST', body: body() });
      const msg = t('email.queued', { time: formatClockOf(r.scheduledAt, i18n.language) });
      announce(msg);
      toast(msg);
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['message-history'] });
      pingDispatcher(true);
      onDone();
    } catch (e) {
      const m = errorText(e, t('email.sendFailed'));
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (address: string, on: boolean) => setTo((cur) => (on ? [...new Set([...cur, address])] : cur.filter((a) => a !== address)));
  const who = c?.tenant ? `${c.tenant.unit} · ${c.tenant.name}` : t('email.composeTitle');

  return (
    <SheetContent
      title={t('email.composeFor', { who })}
      returnFocusTo={returnFocusTo}
      data-testid="compose-sheet"
      footer={
        <>
          <Button variant="secondary" onClick={() => doPreview()} disabled={busy || !code || !to.length} data-testid="compose-preview">
            {t('email.preview')}
          </Button>
          <Button onClick={() => send()} disabled={busy || stale || !!preview?.blocked} data-testid="compose-send">
            {t('email.send')}
          </Button>
        </>
      }
    >
      {ctx.isLoading ? <p className="help">{t('app.loading')}</p> : null}
      {ctx.isError ? (
        <p className="warn" role="alert">
          {t('email.loadFailed')}
        </p>
      ) : null}
      {c ? (
        <div style={{ display: 'grid', gap: 14 }}>
          <div className="field">
            <label htmlFor="compose-template">{t('email.whichEmail')}</label>
            <select id="compose-template" className="input" value={code} onChange={(e) => setCode(e.target.value)}>
              {templates.map((x) => (
                <option key={x.code} value={x.code}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          <fieldset className="field">
            <legend>{t('email.to')}</legend>
            {c.recipients.length === 0 ? (
              <p className="help">
                {t('email.noRecipients')} <ChangeInSettings code="EMAIL_INTERNAL_RECIPIENTS" />
              </p>
            ) : null}
            {c.recipients.map((r) => (
              <label key={`${r.kind}-${r.address}`} className="check" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0' }}>
                <input type="checkbox" checked={to.includes(r.address)} disabled={!r.allowed} onChange={(e) => toggle(r.address, e.target.checked)} />
                <span>
                  <b>{r.kind === 'tenant' ? r.name : r.address}</b>
                  <span className="help" style={{ display: 'block' }}>
                    {r.kind === 'tenant' ? (r.allowed ? r.address : t('email.tenantNoEmail')) : t('email.internal')}
                  </span>
                </span>
              </label>
            ))}
            {c.tenant && !c.tenant.email ? <p className="help">{t('email.tenantNoAddress')}</p> : null}
          </fieldset>
          <div className="field">
            <label htmlFor="compose-lang">{t('email.language')}</label>
            <select id="compose-lang" className="input" value={lang} onChange={(e) => setLang(e.target.value as 'en' | 'es')}>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
            <span className="help" data-testid="compose-lang-help">
              {t('oneLang.composeNote')}
            </span>
            {preview?.noSpanishText ? (
              <span className="help" role="status" data-testid="compose-no-spanish">
                {t('oneLang.noSpanishText')}
              </span>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="compose-message">{t('email.message')}</label>
            <textarea id="compose-message" className="input" value={message} onChange={(e) => setMessage(e.target.value)} aria-describedby="compose-message-help" />
            <span id="compose-message-help" className="help">
              {t('email.messageHelp')}
            </span>
          </div>
          <SpanishWording count={lang === 'es' ? 1 : 0} value={messageEs} onChange={setMessageEs} rows={4} maxLength={4000} testId="compose-message-es" id="compose-message-es" />
          {preview?.noSpanishWording && !stale ? (
            <p className="help" role="status" data-testid="compose-no-wording">
              {t('oneLang.lineNoText', { count: 1 })}
            </p>
          ) : null}
          <p className="help">{c.windowMinutes === null ? t('email.windowMorning') : t('email.window', { count: c.windowMinutes })}</p>
          {error ? (
            <p className="warn" role="alert">
              {error}
            </p>
          ) : null}
          {preview ? (
            <section className="email-preview" aria-labelledby="h-compose-preview" data-testid="compose-preview-panel">
              <h3 id="h-compose-preview">{t('email.previewTitle')}</h3>
              {stale ? <p className="help">{t('email.previewStale')}</p> : null}
              {preview.blocked ? (
                <p className="warn" role="alert" data-testid="compose-blocked">
                  {preview.blocked}
                </p>
              ) : null}
              <div className="email-meta">
                <div>
                  <span className="lbl">{t('email.subject')}</span> <span data-testid="preview-subject">{preview.subject}</span>
                </div>
                <div>
                  <span className="lbl">{t('email.to')}</span> {to.join(', ')}
                </div>
              </div>
              <div className="email-body" lang={preview.lang} style={{ whiteSpace: 'pre-wrap' }} data-testid="preview-body">
                {splitHeading(preview.body).heading ? <p className="email-heading">{splitHeading(preview.body).heading}</p> : null}
                {splitHeading(preview.body).body.replace(/^>\s?/gm, '  ')}
              </div>
              <ReadAloud key={`${preview.lang}-${preview.subject}`} text={`${preview.subject}\n${preview.body}`} lang={preview.lang === 'es' ? 'es' : 'en'} testId="read-preview" />
              {preview.buttons.length ? (
                <ul className="help" aria-label={t('email.buttonsAdded')}>
                  {preview.buttons.map((b) => (
                    <li key={b}>{t(`email.button.${b}`)}</li>
                  ))}
                </ul>
              ) : null}
              {preview.missing.length ? <p className="help">{t('email.missing', { tokens: preview.missing.map((m) => `[${m}]`).join(', ') })}</p> : null}
              {preview.templateSource === 'app' ? <p className="help">{t('email.appWording')}</p> : null}
            </section>
          ) : null}
        </div>
      ) : null}
    </SheetContent>
  );
}
